import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    window.scrollTo(0, 0);
    axios.get(`${API}/api/blog/posts/${slug}`)
      .then(res => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    document.title = `${post.title} | Last Mile Advisors`;
    setMeta('description', post.meta_description || post.excerpt || '');
    if (post.keywords) setMeta('keywords', post.keywords);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${window.location.origin}/blog/${post.slug}`);

    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'blog-jsonld';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.meta_description,
      image: post.cover_image,
      author: { '@type': 'Person', name: post.author },
      publisher: { '@type': 'Organization', name: 'Last Mile Advisors' },
      datePublished: post.published_at,
      dateModified: post.updated_at,
      mainEntityOfPage: `${window.location.origin}/blog/${post.slug}`,
    });
    document.head.appendChild(ld);

    return () => {
      document.getElementById('blog-jsonld')?.remove();
      document.title = 'Last Mile Advisors';
    };
  }, [post]);

  if (loading) {
    return (
      <main className="page-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="page-content" style={{ textAlign: 'center', padding: '6rem 1rem' }}>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', marginBottom: '1rem' }}>Post not found</h1>
        <Link to="/blog"><Button className="btn-primary">Back to Blog</Button></Link>
      </main>
    );
  }

  return (
    <main className="page-content">
      <article className="blog-post" data-testid="blog-post">
        <div className="blog-post-header">
          <div className="container blog-post-container">
            <Link to="/blog" className="blog-back-link" data-testid="blog-back-link">
              <ArrowLeft size={16} /> All posts
            </Link>
            <div className="blog-card-meta" style={{ marginBottom: '1rem' }}>
              <span className="blog-card-category">{post.category}</span>
              <span className="blog-card-readtime"><Clock size={12} /> {post.read_time} min read</span>
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            <p className="blog-post-byline">By {post.author} · {formatDate(post.published_at)}</p>
          </div>
        </div>

        {post.cover_image && (
          <div className="container blog-post-container">
            <div className="blog-post-cover">
              <img src={post.cover_image} alt={post.title} />
            </div>
          </div>
        )}

        <div className="container blog-post-container">
          <div className="blog-post-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>

          <div className="blog-post-cta">
            <h3>Ready to lower your energy costs?</h3>
            <p>Last Mile Advisors shops 50+ suppliers to find your lowest rate — at zero cost to you.</p>
            <Link to="/contact"><Button className="btn-primary" data-testid="blog-cta-btn">Request a Free Rate Analysis</Button></Link>
          </div>
        </div>
      </article>
    </main>
  );
}
